import { cn } from '@/lib/utils'

import greeting from '@/assets/mascot/mascot-greeting.png'
import laughing from '@/assets/mascot/mascot-laughing.png'
import lecturing from '@/assets/mascot/mascot-lecturing.png'
import money from '@/assets/mascot/mascot-money.png'
import shocked from '@/assets/mascot/mascot-shocked.png'
import standing from '@/assets/mascot/mascot-standing.png'
import teaching from '@/assets/mascot/mascot-teaching.png'
import warning from '@/assets/mascot/mascot-warning.png'
import winking from '@/assets/mascot/mascot-winking.png'

/**
 * Die Posen des Waschbären.
 *
 * "sneaky" fehlt hier bewusst: Das Bild hat als einziges einen deckenden
 * Hintergrund und ist das GLÜXXIT-Logo, kein schwebendes Maskottchen. Als Pose
 * verwendet erschiene es als grauer Kasten. Es liegt unter assets/brand.
 */
export type MascotPose =
    | 'greeting'
    | 'winking'
    | 'teaching'
    | 'warning'
    | 'laughing'
    | 'shocked'
    | 'lecturing'
    | 'standing'
    | 'money'

const POSES: Record<MascotPose, string> = {
    greeting,
    winking,
    teaching,
    warning,
    laughing,
    shocked,
    lecturing,
    standing,
    money,
}

interface MascotSlotProps {
    pose?: MascotPose
    size?: number
    className?: string
    alt?: string
}

/** Der Waschbär als Bild. Quelle: clickdummy MascotSlot. */
export function MascotSlot({ pose = 'greeting', size = 140, className, alt }: MascotSlotProps) {
    return (
        <img
            src={POSES[pose]}
            alt={alt ?? ''}
            aria-hidden={alt ? undefined : true}
            draggable={false}
            width={size}
            height={size}
            className={cn(
                '[pointer-events:none] block object-contain [filter:drop-shadow(0_6px_14px_rgba(0,0,0,.35))] select-none',
                className,
            )}
            style={{ width: size, height: size }}
        />
    )
}

export type BubbleSide = 'right' | 'left' | 'above'

interface MascotBubbleProps {
    children: React.ReactNode
    /**
     * Wo die Blase relativ zum Waschbären steht. Der Zeiger zeigt IMMER zu ihm:
     *   'right' = Blase rechts neben ihm → Zeiger links, waagerecht
     *   'left'  = Blase links neben ihm  → Zeiger rechts, waagerecht (gespiegelt)
     *   'above' = Blase über ihm         → Zeiger unten links, nach unten
     */
    side?: BubbleSide
    className?: string
}

/** Sprechblase im Handschrift-Look. Quelle: clickdummy MascotBubble. */
export function MascotBubble({ children, side = 'right', className }: MascotBubbleProps) {
    return (
        <div
            data-side={side}
            className={cn(
                'relative rounded-[18px] bg-white px-4 py-3.5 font-wb-hand text-[22px]',
                // Neben dem Waschbaeren nimmt die Blase den Rest der Zeile - auf
                // einem 390 Pixel breiten Handy blieb sonst rechts ein
                // Handbreit ungenutzt. Ueber ihm steht sie fuer sich und
                // braucht eine Bremse, sonst wird die Zeile zu lang zum Lesen.
                side === 'above' ? 'max-w-60' : 'min-w-0 flex-1',
                'leading-tight font-bold text-[#0d1320]',
                'shadow-[0_8px_24px_rgba(0,0,0,.4),0_0_0_3px_var(--color-wb-accent)]',
                className,
            )}
        >
            {children}
            <BubbleTail side={side} />
        </div>
    )
}

function BubbleTail({ side }: { side: BubbleSide }) {
    if (side === 'above') {
        return (
            <svg
                width="22"
                height="18"
                viewBox="0 0 22 18"
                aria-hidden="true"
                data-testid="bubble-tail"
                data-direction="down"
                className="absolute -bottom-4 left-[22px]"
            >
                <path d="M2 0 L22 0 L4 17 Z" fill="var(--color-wb-accent)" />
                <path d="M5 0 L22 0 L7 14 Z" fill="#fff" />
            </svg>
        )
    }

    return (
        <svg
            width="18"
            height="22"
            viewBox="0 0 18 22"
            aria-hidden="true"
            data-testid="bubble-tail"
            data-direction={side === 'left' ? 'right' : 'left'}
            // Mittig an der Blase: Waschbaer und Blase stehen vertikal
            // zentriert nebeneinander (WETTBANDIT-58), der Zeiger trifft ihn so
            // auf Hoehe seiner Mitte - egal wie viele Zeilen die Blase hat.
            className={cn(
                'absolute top-1/2 -translate-y-1/2',
                side === 'left' ? '-right-[15px] -scale-x-100' : '-left-[15px]',
            )}
        >
            <path d="M18 1 L18 21 L0 17 Z" fill="var(--color-wb-accent)" />
            <path d="M18 4.5 L18 17.5 L4.5 14.5 Z" fill="#fff" />
        </svg>
    )
}
